import React, { useState } from "react";
import { PhotoIcon } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import api from "../services/axios";

const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp"];

const CLASSES = [
  "6ème",
  "5ème",
  "4ème",
  "3ème",
  "2nde",
  "1ère",
  "Terminale",
  "CP",
  "CE1",
  "CE2",
  "CM1",
  "CM2",
];
const MATIERES = [
  "Français",
  "Mathématiques",
  "Physique Chimie",
  "Sciences de la vie et de la Terre",
  "Anglais",
  "Espagnol",
  "Allemand",
  "Chinois",
  "Latin",
  "Histoire-géographie",
  "Sciences économiques et sociales",
];
const ETATS = ["Neuf", "Très bon état", "Bon état", "Usé"];

export default function DeposerLivrePage() {
  const navigate = useNavigate();
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    titre: "",
    classe: "",
    matière: "",
    autreMatière: "",
    édition: "",
    année: "",
    état: "",
    infos_supplémentaires: "",
    prix: "",
    disponibilité: true,
    phoneNumber: "",
  });

  const validateImage = (file: File): string | null => {
    if (!ALLOWED_MIME.includes(file.type)) {
      return "Erreur : seuls les fichiers JPG, PNG ou WebP sont acceptés.";
    }

    if (file.size > 2 * 1024 * 1024) {
      return "Erreur : image trop lourde (max 2 Mo).";
    }
    return null;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateImage(file);
    if (error) {
      toast.error(error);
      setImage(null);
      setPreview(null);
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    let newValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    if (name === "phoneNumber") {
      newValue = value.replace(/[^0-9+ ]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let imageUrl = "";
      if (image) {
        const formImage = new FormData();
        formImage.append("image", image);

        const imageRes = await api.post("/minio/image", formImage);
        imageUrl = imageRes.data.imageUrl;
      }

      const bookData = {
        title: formData.titre,
        gradeLevel: formData.classe,
        subject:
          formData.matière === "autre"
            ? formData.autreMatière
            : formData.matière,
        edition: formData.édition,
        year: parseInt(formData.année),
        condition: formData.état,
        imageUrl,
        available: formData.disponibilité,
        price: parseFloat(formData.prix),
        additionalInfo: formData.infos_supplémentaires,
        phoneNumber: formData.phoneNumber,
      };

      const secondToken = localStorage.getItem("secondAuthToken");

      await api.post("/books", bookData, {
        headers: {
          "second-auth-token": secondToken ?? "",
        },
      });

      if (window.gtag) {
        window.gtag('event', 'book_deposited', {
          'event_category': 'Book Submission',
          'event_label': bookData.title,
          'value': bookData.price
        });
      }

      toast.info("Livre déposé avec succès !");
      setFormData({
        titre: "",
        classe: "",
        matière: "",
        autreMatière: "",
        édition: "",
        année: "",
        état: "",
        infos_supplémentaires: "",
        prix: "",
        disponibilité: true,
        phoneNumber: "",
      });
      setImage(null);
      setPreview(null);
    } catch (error) {
      toast.error("Erreur lors du dépôt ");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6  py-10">
      <h2 className="text-2xl font-bold mb-4 text-start">Déposer un livre</h2>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4"
      >
        <div className="space-y-3">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Titre
            </label>
            <input
              type="text"
              name="titre"
              value={formData.titre}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>

          {/* Classe */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Classe
            </label>
            <select
              name="classe"
              value={formData.classe}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Sélectionner une classe</option>
              {CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Matière */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Matière
            </label>
            <select
              name="matière"
              value={formData.matière}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Sélectionner une matière</option>
              {MATIERES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value="autre">Autre...</option>
            </select>
            {formData.matière === "autre" && (
              <input
                type="text"
                name="autreMatière"
                placeholder="Entrez la matière"
                value={formData.autreMatière}
                onChange={handleChange}
                required
                className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2"
              />
            )}
          </div>

          {/* Édition */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Édition
            </label>
            <input
              type="text"
              name="édition"
              value={formData.édition}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>

          {/* Année */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Année
            </label>
            <input
              type="number"
              name="année"
              value={formData.année}
              min="2000"
              max={new Date().getFullYear()}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900">
              Numéro de téléphone
            </label>
            <input
              type="tel"
              name="phoneNumber"
              inputMode="numeric"
              pattern="[0-9 +]*"
              placeholder="06 XX XX XX XX"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>
        </div>

        <div className="space-y-3">
          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Image (optionnelle)
            </label>
            <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-300 px-6 py-10 h-36 w-full overflow-hidden">
              <div className="text-center w-full h-full flex flex-col items-center justify-center">
                {preview ? (
                  <img
                    src={preview}
                    alt="Preview"
                    className="h-full w-full object-cover rounded-md"
                  />
                ) : (
                  <PhotoIcon
                    className="h-12 w-12 text-gray-400"
                    aria-hidden="true"
                  />
                )}
                <div className="mt-4 flex text-sm text-gray-600 justify-center">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    <span>Choisir un fichier</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="sr-only"
                      onChange={handleImageChange}
                    />
                  </label>
                  <p className="pl-1">ou glisser-déposer</p>
                </div>
                <p className="text-xs text-gray-500">
                  JPG, PNG ou WebP (max. 2 Mo)
                </p>
              </div>
            </div>
          </div>

          {/* Infos supplémentaires */}
          <div>
            <label
              htmlFor="infos_supplémentaires"
              className="block text-sm font-medium text-gray-900"
            >
              Informations supplémentaires
            </label>
            <textarea
              name="infos_supplémentaires"
              id="infos_supplémentaires"
              rows={2}
              placeholder="Écrivez ici..."
              value={formData.infos_supplémentaires}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600"
            ></textarea>
          </div>

          {/* Prix */}
          <div>
            <label
              htmlFor="prix"
              className="block text-sm font-medium text-gray-900"
            >
              Prix (€)
            </label>
            <input
              type="number"
              name="prix"
              id="prix"
              placeholder="Prix (€)"
              value={formData.prix}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            />
          </div>

          {/* État */}
          <div>
            <label className="block text-sm font-medium text-gray-900">
              État
            </label>
            <select
              name="état"
              value={formData.état}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
            >
              <option value="">Sélectionner un état</option>
              {ETATS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>

          {/* Disponible */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="disponibilité"
              checked={formData.disponibilité}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label
              htmlFor="disponibilité"
              className="ml-2 block text-sm text-gray-900"
            >
              Disponible
            </label>
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 flex justify-center">
          <button
            type="submit"
            className="w-full sm:w-1/2 max-w-sm rounded-md bg-indigo-600 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
          >
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}
